import React, { useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { CFCProps } from 'types';
import { RootState } from 'redux/root.reducer';
import { AirportLocation16, Close16 } from '@carbon/icons-react';
import {
  Button,
  ContentSwitcher,
  Switch,
  ContentSwitcherOnChangeData
} from 'carbon-components-react';
import { zoomToCenter, removePin } from 'components/Map/helper';
import { setIsOpenMapLocSearchPanel } from 'redux/map/actions';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import AddressSearch from './addressSearch';
import LatLongSearch from './latLongSearch';
import LotNumberSearch from './lotNumberSearch';

const MapLocationSearchPanel: React.FC<Props> = ({ isOpen }) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentMode, setCurrentMode] = useState<string>('address');

  const onClickCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const center = [position.coords.longitude, position.coords.latitude];
      zoomToCenter(center);
    });
    closeSearch();
  };

  const closeSearch = () => {
    dispatch(setIsOpenMapLocSearchPanel(false));
  };

  const handleClickOutside = (evt: any) => {
    if (containerRef.current && evt.target) {
      if (!containerRef.current.contains(evt.target)) {
        closeSearch();
      }
    }
  };

  const renderSearchInput = (type: string) => {
    switch (type) {
      case 'address':
        return <AddressSearch onClose={() => closeSearch()} />;
      case 'lot':
        return <LotNumberSearch onClose={() => closeSearch()} />;
      case 'gps':
        return <LatLongSearch onClose={() => closeSearch()} />;
    }
  };

  const getSelectedIndex = (type: string) => {
    switch (type) {
      case 'address':
        return 0;
      case 'lot':
        return 1;
      case 'gps':
        return 2;
    }
  };

  useEventListener('mousedown', handleClickOutside);
  useEventListener('touchstart', handleClickOutside);

  useComponentMount(() => {
    return () => {
      removePin();
    };
  });

  return (
    <>
      {isOpen && (
        <div className="mls-modal">
          <div className="mls-panel" ref={containerRef}>
            <div className="mls-panel-header">
              <div className="mls-panel-header-title">Find location</div>
              <Button size="small" kind="ghost" onClick={() => closeSearch()}>
                <Close16 />
              </Button>
            </div>
            <div className="mls-panel-body">
              <ContentSwitcher
                size="sm"
                selectedIndex={getSelectedIndex(currentMode)}
                onChange={(item: ContentSwitcherOnChangeData) =>
                  setCurrentMode(
                    typeof item.name === 'string' ? item.name : 'address'
                  )
                }
              >
                <Switch name={'address'} text="Address" />
                <Switch name={'lot'} text="Lot number" />
                <Switch name={'gps'} text="Lat, Long" />
              </ContentSwitcher>
              <div className="mls-panel-body-input">
                {renderSearchInput(currentMode)}
              </div>
            </div>
            <div className="mls-panel-footer">
              <Button
                renderIcon={AirportLocation16}
                kind="tertiary"
                className="mls-current-loc-btn"
                onClick={() => onClickCurrentLocation()}
              >
                Use my current location
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = ({ mapCache }: RootState) => ({
  isOpen: mapCache.isOpenMapLocSearchPanel
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default connector(MapLocationSearchPanel);
